<script lang="ts">
	// Modeled on the NewInventoryModal component in Pro Web: pick a part type (or click a frequently used one) to create a part.
	// It doesn't know how to create the part, the `createPart` callback does that.
	import type { i18n } from 'types/common'

	import { getContext, tick } from 'svelte'
	import Modal from '@isoftdata/svelte-modal'
	import Button from '@isoftdata/svelte-button'
	import Autocomplete from '@isoftdata/svelte-autocomplete'

	interface InventoryTypeOption {
		id: number
		name: string
	}

	interface Props {
		/** Every part type that can be picked */
		inventoryTypes: Array<InventoryTypeOption>
		/** Shown as buttons that create a part with a single click. Leave empty to hide the section. */
		frequentlyUsedInventoryTypes?: Array<InventoryTypeOption>
		/** Should resolve to true if the part was created, which closes the modal. Otherwise the modal stays open. */
		createPart: (inventoryTypeId: number) => Promise<boolean>
	}

	let { inventoryTypes, frequentlyUsedInventoryTypes = [], createPart }: Props = $props()

	let show = $state(false)
	let inventoryType: InventoryTypeOption | null = $state(null)
	let savingInProgress = $state(false)

	const { t: translate } = getContext<i18n>('i18next')

	const inputId = 'newPartInventoryTypeInput'

	export async function open() {
		savingInProgress = false
		inventoryType = null
		show = true
		await tick()
		document.getElementById(inputId)?.focus()
	}

	async function create(inventoryTypeId: number | undefined) {
		if (!inventoryTypeId || savingInProgress) {
			return
		}
		savingInProgress = true
		try {
			if (await createPart(inventoryTypeId)) {
				show = false
			}
		} finally {
			savingInProgress = false
		}
	}
</script>

<Modal
	closeShown={false}
	backdropClickCancels={false}
	{savingInProgress}
	title={translate('inventoryBasic.createNewPart', 'Create New Part')}
	subtitle={translate('inventoryBasic.enterPartType', 'Enter a part type to get started')}
	confirmButtonText={translate('common:createPart', 'Create Part')}
	confirmButtonDisabled={!inventoryType}
	cancelButtonText={translate('common:cancel', 'Cancel')}
	bind:show
	close={() => (show = false)}
	confirm={() => create(inventoryType?.id)}
>
	<Autocomplete
		id={inputId}
		label={translate('common:inventoryType', 'Inventory Type')}
		options={inventoryTypes}
		emptyValue={null}
		bind:value={inventoryType}
		getLabel={type => (type ? `${type.name} - ${type.id}` : '')}
	/>

	{#if frequentlyUsedInventoryTypes.length > 0}
		<hr />
		<h5 class="mb-0">{translate('inventoryBasic.frequentlyUsedPartTypes', 'Frequently used part types')}</h5>
		<small>{translate('inventoryBasic.clickToCreate', 'Click to create')}</small>
		<div class="row mt-3">
			{#each frequentlyUsedInventoryTypes as type (type.id)}
				<div class="col-12 col-md-6 mb-2">
					<Button
						outline
						size="sm"
						class="w-100"
						disabled={savingInProgress}
						onclick={() => create(type.id)}>{type.name}</Button
					>
				</div>
			{/each}
		</div>
	{/if}
</Modal>
